/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Count } from '../models/count';
import { Hotel } from '../models/hotel';
import { HotelPartial } from '../models/hotel-partial';
import { HotelWithRelations } from '../models/hotel-with-relations';
import { NewHotel } from '../models/new-hotel';

@Injectable({
  providedIn: 'root',
})
export class HotelControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation hotelControllerCreateAll
   */
  static readonly HotelControllerCreateAllPath = '/hotels/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll$Response(params?: {
    body?: Array<NewHotel>
  }): Observable<StrictHttpResponse<Array<Hotel>>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Hotel>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAll(params?: {
    body?: Array<NewHotel>
  }): Observable<Array<Hotel>> {

    return this.createAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Hotel>>) => r.body as Array<Hotel>)
    );
  }

  /**
   * Path part for operation hotelControllerCount
   */
  static readonly HotelControllerCountPath = '/hotels/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `count()` instead.
   *
   * This method doesn't expect any request body.
   */
  count$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `count$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  count(params?: {
    where?: any;
  }): Observable<Count> {

    return this.count$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation hotelControllerFindById
   */
  static readonly HotelControllerFindByIdPath = '/hotels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<HotelWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<HotelWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    id: string;
    filter?: any;
  }): Observable<HotelWithRelations> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<HotelWithRelations>) => r.body as HotelWithRelations)
    );
  }

  /**
   * Path part for operation hotelControllerReplaceById
   */
  static readonly HotelControllerReplaceByIdPath = '/hotels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `replaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById$Response(params: {
    id: string;
    body?: Hotel
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `replaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceById(params: {
    id: string;
    body?: Hotel
  }): Observable<void> {

    return this.replaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation hotelControllerDeleteById
   */
  static readonly HotelControllerDeleteByIdPath = '/hotels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById(params: {
    id: string;
  }): Observable<void> {

    return this.deleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation hotelControllerUpdateById
   */
  static readonly HotelControllerUpdateByIdPath = '/hotels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    id: string;
    body?: HotelPartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    id: string;
    body?: HotelPartial
  }): Observable<void> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation hotelControllerFind
   */
  static readonly HotelControllerFindPath = '/hotels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `find()` instead.
   *
   * This method doesn't expect any request body.
   */
  find$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<HotelWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<HotelWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `find$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  find(params?: {
    filter?: any;
  }): Observable<Array<HotelWithRelations>> {

    return this.find$Response(params).pipe(
      map((r: StrictHttpResponse<Array<HotelWithRelations>>) => r.body as Array<HotelWithRelations>)
    );
  }

  /**
   * Path part for operation hotelControllerCreate
   */
  static readonly HotelControllerCreatePath = '/hotels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params?: {
    body?: NewHotel
  }): Observable<StrictHttpResponse<Hotel>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Hotel>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params?: {
    body?: NewHotel
  }): Observable<Hotel> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<Hotel>) => r.body as Hotel)
    );
  }

  /**
   * Path part for operation hotelControllerUpdateAll
   */
  static readonly HotelControllerUpdateAllPath = '/hotels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAll$Response(params?: {
    where?: any;
    body?: HotelPartial
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerUpdateAllPath, 'patch');
    if (params) {
      rb.query('where', params.where, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAll(params?: {
    where?: any;
    body?: HotelPartial
  }): Observable<Count> {

    return this.updateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation hotelControllerProxyCreateAll
   */
  static readonly HotelControllerProxyCreateAllPath = '/proxy/hotels/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll$Response(params?: {
    body?: Array<NewHotel>
  }): Observable<StrictHttpResponse<Array<Hotel>>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerProxyCreateAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<Hotel>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreateAll(params?: {
    body?: Array<NewHotel>
  }): Observable<Array<Hotel>> {

    return this.proxyCreateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Array<Hotel>>) => r.body as Array<Hotel>)
    );
  }

  /**
   * Path part for operation hotelControllerProxyCount
   */
  static readonly HotelControllerProxyCountPath = '/proxy/hotels/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCount()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerProxyCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyCount(params?: {
    where?: any;
  }): Observable<Count> {

    return this.proxyCount$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

  /**
   * Path part for operation hotelControllerProxyFindById
   */
  static readonly HotelControllerProxyFindByIdPath = '/proxy/hotels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFindById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<HotelWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerProxyFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<HotelWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFindById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFindById(params: {
    id: string;
    filter?: any;
  }): Observable<HotelWithRelations> {

    return this.proxyFindById$Response(params).pipe(
      map((r: StrictHttpResponse<HotelWithRelations>) => r.body as HotelWithRelations)
    );
  }

  /**
   * Path part for operation hotelControllerProxyReplaceById
   */
  static readonly HotelControllerProxyReplaceByIdPath = '/proxy/hotels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyReplaceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById$Response(params: {
    id: string;
    body?: Hotel
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerProxyReplaceByIdPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyReplaceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyReplaceById(params: {
    id: string;
    body?: Hotel
  }): Observable<void> {

    return this.proxyReplaceById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation hotelControllerProxyDeleteById
   */
  static readonly HotelControllerProxyDeleteByIdPath = '/proxy/hotels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyDeleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerProxyDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyDeleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyDeleteById(params: {
    id: string;
  }): Observable<void> {

    return this.proxyDeleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation hotelControllerProxyUpdateById
   */
  static readonly HotelControllerProxyUpdateByIdPath = '/proxy/hotels/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyUpdateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById$Response(params: {
    id: string;
    body?: HotelPartial
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerProxyUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyUpdateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateById(params: {
    id: string;
    body?: HotelPartial
  }): Observable<void> {

    return this.proxyUpdateById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation hotelControllerProxyFind
   */
  static readonly HotelControllerProxyFindPath = '/proxy/hotels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyFind()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<HotelWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerProxyFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<HotelWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyFind$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  proxyFind(params?: {
    filter?: any;
  }): Observable<Array<HotelWithRelations>> {

    return this.proxyFind$Response(params).pipe(
      map((r: StrictHttpResponse<Array<HotelWithRelations>>) => r.body as Array<HotelWithRelations>)
    );
  }

  /**
   * Path part for operation hotelControllerProxyCreate
   */
  static readonly HotelControllerProxyCreatePath = '/proxy/hotels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate$Response(params?: {
    body?: NewHotel
  }): Observable<StrictHttpResponse<Hotel>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerProxyCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Hotel>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyCreate(params?: {
    body?: NewHotel
  }): Observable<Hotel> {

    return this.proxyCreate$Response(params).pipe(
      map((r: StrictHttpResponse<Hotel>) => r.body as Hotel)
    );
  }

  /**
   * Path part for operation hotelControllerProxyUpdateAll
   */
  static readonly HotelControllerProxyUpdateAllPath = '/proxy/hotels';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `proxyUpdateAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateAll$Response(params?: {
    where?: any;
    body?: HotelPartial
  }): Observable<StrictHttpResponse<Count>> {

    const rb = new RequestBuilder(this.rootUrl, HotelControllerService.HotelControllerProxyUpdateAllPath, 'patch');
    if (params) {
      rb.query('where', params.where, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Count>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `proxyUpdateAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  proxyUpdateAll(params?: {
    where?: any;
    body?: HotelPartial
  }): Observable<Count> {

    return this.proxyUpdateAll$Response(params).pipe(
      map((r: StrictHttpResponse<Count>) => r.body as Count)
    );
  }

}
